import { useViewAs } from "@/hooks/useViewAs";
import { useEffect, useState } from "react";

// The data platform's files (24 Oct 2026), written to /platform/ at deploy by
// scripts/build-platform.mjs from the record greeksoup.ai keeps: market.json (the
// US market page), datasets/<slug>.json (each dataset's newest 500 rows) and
// companies/<symbol>.json (one company's whole record). Pages fetch them from the
// same site, so nothing waits on another server.

/** His ruling, 24 Oct 2026 (replacing 16 Oct): show the record counts, with their dates, and say coverage is expanding. */
export const SHOW_COUNTS = true;

/** S&P's own wording for the three indices we cover (spglobal.com/spdji, S&P Composite 1500, checked 24 Oct 2026). */
export const SP1500 = {
  text: "The S&P 500, S&P MidCap 400 and S&P SmallCap 600 together cover approximately 90% of U.S. market capitalization.",
  source: "https://www.spglobal.com/spdji/en/indices/equity/sp-composite-1500/",
};

export interface Market {
  version: 1;
  built: string;
  companies: number;
  counts: Record<"insider" | "planned" | "holders" | "short" | "congress" | "events" | "filings" | "years" | "segments", number> & { funds?: number; fund_filers?: number };
  asof: Record<string, string | null>;
  last30: Record<string, number>;
  starts: Record<string, string | null>;
  latest: Latest[];
  breakdown: { forms: KN[]; events: KN[]; insider: KN[]; holders: KN[] };
  top: { symbol: string; name: string; n: number }[];
  by_year: { filings: Record<string, number>; events: Record<string, number> };
  /** The 13F funds (30 Oct 2026): the quarter most funds have filed for, and how many funds file. */
  funds?: { quarter: string | null; filers: number } | null;
}
export interface KN { k: string; n: number }
export interface Latest { symbol: string; name: string; kind: string; what: string; at: string; accepted?: string | null; url: string | null }
export interface DatasetFile { version: 1; built: string; dataset: string; total: number; asof: string | null; rows: (Latest & { row: Record<string, unknown> })[]; by_day: Record<string, number> }
export interface Tab { cols: string[]; rows: unknown[][] }
export interface CompanyFile {
  version: 1;
  company: { symbol: string; name: string; exchange: string; industry: string; cik: number | null; fye: string };
  metrics: Record<string, number | null>;
  years: Tab; quarters: Tab;
  segments: Record<string, { years: string[]; rows: { label: string; values: Record<string, number> }[]; last: string }>;
  insider: Tab; planned: Tab; holders: Tab; short: Tab; congress: Tab; events: Tab; filings: Tab;
  counts: Record<string, number>;
  by_year: { filings: Record<string, number>; events: Record<string, number> };
  access?: Access;
}

/** A table's rows as objects, keyed by its column names. */
export const objs = <T = Record<string, unknown>>(t?: Tab): T[] =>
  t ? t.rows.map((r) => Object.fromEntries(t.cols.map((c, i) => [c, r[i]])) as T) : [];

// A company's files (companies/, more/, deep/) come in two views (28 Oct 2026): the last 5 fiscal years for
// everyone, the full record for a reader with a plan. The worker decides from the sign-in sent with the request.
const RECORD = /^(companies|more|deep)\//;
const signIn = async (): Promise<string | null> => {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    return (await supabase.auth.getSession()).data.session?.access_token ?? null;
  } catch { return null; }
};
const cache = new Map<string, Promise<unknown>>();
// an admin "viewing as" a member sees the record a free account gets
const viewingAs = () => { try { return !!sessionStorage.getItem("admin-view-as-role"); } catch { return false; } };
export const load = async <T,>(path: string): Promise<T> => {
  const token = RECORD.test(path) && !viewingAs() ? await signIn() : null;
  const key = `${token ? "signed" : "free"}:${path}`;
  if (!cache.has(key)) cache.set(key, fetch(`/platform/${path}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); }).catch((e) => { cache.delete(key); throw e; }));
  return cache.get(key) as Promise<T>;
};

/** How much of the record a company file carries: the free 5 years, or the whole of it. */
export interface Access { full: boolean; years: number; from: string | null; full_from: string | null; locked_before?: string }

/** One platform file: undefined while loading, null when it could not be read. */
export const usePlatform = <T,>(path: string | null) => {
  const [v, setV] = useState<T | null | undefined>(undefined);
  const { role } = useViewAs();
  useEffect(() => {
    if (!path) return;
    let live = true;
    setV(undefined);
    load<T>(path).then((d) => live && setV(d), () => live && setV(null));
    return () => { live = false; };
  }, [path, role]);
  return v;
};

/** One 13F fund (funds/<slug>.json): its latest quarter's holdings, each with its move on the quarter before, and every quarter it filed. */
export interface FundFile {
  version: 1;
  fund: { cik: number; name: string; slug: string; former: string[]; period: string; filed: string; url: string; prev_period: string | null; value: number; positions: number };
  counts: Record<string, number>;
  history: Tab;
  holdings: Tab;
}
/** Every 13F fund, largest first (funds/index.json). */
export interface FundIndex { version: 1; quarter: string | null; cols: string[]; funds: [cik: number, name: string, slug: string, value: number, period: string, filed: string, positions: number][] }
/** Every fund holding one company (holders/<symbol>.json); a fund with a page is named through FundIndex. */
export interface HoldersFile extends Tab { version: 1; symbol: string; period: string; prev: string; count: number }
export const fundPath = (slug: string) => `/data/funds/${slug}`;
/** A 13F quarter end as a reader says it: "June 2026 quarter". */
export const quarterOf = (iso?: string | null) => (iso ? `${["March", "June", "September", "December"][Math.floor((+iso.slice(5, 7) - 1) / 3)]} ${iso.slice(0, 4)} quarter` : "–");

export const fileOf = (symbol: string) => `companies/${symbol.toLowerCase().replace(/\./g, "-")}.json`;

// ---- formatting ----
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const day = (iso?: string | null, year = true) => {
  if (!iso) return "–";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${+d} ${MON[+m - 1]}${year ? " " + y : ""}`;
};
export const monthYear = (iso?: string | null) => {
  if (!iso) return "–";
  const [y, m] = iso.split("-");
  return `${MON[+m - 1]} ${y}`;
};
/** The SEC's acceptance time, shown in New York time: "23 Oct 2026, 5:28 pm ET". */
export const et = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(+d)) return null;
  return d.toLocaleString("en-US", { timeZone: "America/New_York", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).replace(/^(\w+) (\d+),/, "$2 $1,") + " ET";
};
export const n0 = (v?: number | null) => (v == null ? "–" : Math.round(v).toLocaleString("en-US"));
export const compact = (v?: number | null) => {
  if (v == null) return "–";
  const a = Math.abs(v);
  return a >= 1e12 ? `${(v / 1e12).toFixed(2)}T` : a >= 1e9 ? `${(v / 1e9).toFixed(1)}B` : a >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${Math.round(v)}`;
};
export const usd = (v?: number | null) => (v == null ? "–" : `${v < 0 ? "-" : ""}$${compact(Math.abs(v))}`);

/** The datasets the platform has files for, with their counts, start and newest date in market.json. */
export const PLATFORM_SETS = [
  { slug: "events", name: "Company events", short: "8-K events", key: "events", start: "events", unit: "events" },
  { slug: "insider-trades", name: "Insider trades", short: "Form 4 trades", key: "insider", start: "insider", unit: "trades" },
  { slug: "planned-insider-sales", name: "Planned insider sales", short: "Form 144 notices", key: "planned", start: "planned", unit: "notices" },
  { slug: "large-holders", name: "Large holders", short: "13D and 13G filings", key: "holders", start: "holders", unit: "filings" },
  { slug: "congress-trades", name: "Trades by members of Congress", short: "House trades", key: "congress", start: "congress", unit: "trades" },
  { slug: "short-interest", name: "Short interest", short: "Short interest settlements", key: "short", start: "short", unit: "settlements" },
  { slug: "fund-holdings-13f", name: "Fund holdings", short: "13F holdings", key: "funds", start: "funds", unit: "holdings" },
  { slug: "financial-statements", name: "Results and financial statements", short: "Company years of results", key: "years", start: "years", unit: "company years" },
] as const;

export const KIND_TONE: Record<string, string> = {
  Results: "var(--sn-c1)", "Leadership change": "var(--sn-c4)", "Material agreement": "var(--sn-c3)", "Deal completed": "var(--sn-c2)",
  "Shareholder vote": "var(--sn-c5)", "Insider trade": "var(--sn-c2)", "Planned sale": "var(--sn-c5)", "Schedule 13D": "var(--sn-c4)",
  "Schedule 13G": "var(--sn-c3)", "Congress trade": "var(--sn-c1)", "Short interest": "var(--sn-neutral)",
  Purchase: "var(--sn-up)", "Sale under a trading plan": "color-mix(in srgb, var(--sn-down) 55%, transparent)", "Sale outside a trading plan": "var(--sn-down)", Other: "var(--sn-neutral)", "10-Q": "var(--sn-c1)", "10-K": "var(--sn-c1)", "20-F": "var(--sn-c1)", "40-F": "var(--sn-c1)", "6-K": "var(--sn-c1)",
};
export const toneOf = (k: string) => KIND_TONE[k] || "var(--sn-neutral)";

/** Where a dataset's records start, read from what we hold ("From FY2009", "From October 2025"), or the catalogue's line until the file loads. */
const START_KEY: Record<string, [key: string, fiscal: boolean]> = {
  "financial-statements": ["years", true], segments: ["segments", true], "insider-trades": ["insider", false], "planned-insider-sales": ["planned", false],
  "large-holders": ["holders", false], "congress-trades": ["congress", false], "short-interest": ["short", false], events: ["events", false],
  "fund-holdings-13f": ["funds", false],
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const useSince = (slug: string, fallback: string) => {
  const m = usePlatform<Market>("market.json");
  const k = START_KEY[slug];
  const v = k && m ? m.starts[k[0]] : null;
  if (!v) return fallback;
  return k[1] ? `From FY${v.slice(0, 4)}` : `From ${MONTHS[+v.slice(5, 7) - 1]} ${v.slice(0, 4)}`;
};
